import { useEffect, useState, useRef } from 'react';
import { Button, toast } from '@pella/sash-framework';
import QuestionAnswers from '../QuestionAnswers';
import {
  useWorkOrderSummary,
  useMaintenanceFollowUpQuestions,
  useCreateWorkOrderTracking,
} from '../../../hooks/useMaintenanceCopilot';
import { followUpQuestionsToQuestionAnswers } from '../../../utils/functions';
import { answerOptions } from '../QuestionAnswers/answerOptions';
import { useTranslation, Trans } from 'react-i18next';
import CancellationReasonDialog from '../../molecules/CancellationReasonDialog';
import styles from './DescribeProblem.module.css';

const AnyQuestionAnswers = /** @type {import('react').ElementType} */ (QuestionAnswers);

const getCurrentTimestamp = () => Date.now();

/**
 * The screen with follow-up questions
 * @param {object} props - The props for the component.
 * @param {string} props.conversationId - Id of the conversation
 * @param {string} props.equipmentNumber - Equipment number
 * @param {string} props.siteId - Site ID
 * @param {(summaryText: string) => void} props.setSummaryText - On getting the summary text
 * @param {() => void} props.onCloseSummaryGenerationErrorModal - called when the summary generation error modal is closed
 * @param {() => void} props.onCloseRequestCompleted - Called when request is successfully closed
 * @param {import('../../pages/EquipActionPortal/questionTypes').FollowUpQuestion[]} props.followUpQuestions - The questions which appear that user can answer for additional details
 * @param {(followUpQuestions: import('../../pages/EquipActionPortal/questionTypes').FollowUpQuestion[]) => void} props.setFollowUpQuestions - Setting/updating the follow-up questions
 * @param {() => void} props.handleSubmitWorkOrder - Called to submit the work order directly
 * @param {number | null} props.downtimeStartTime - Epoch time when Continue button was clicked
 * @param {(data: {outcome: string, followUpQuestions: Array, arrivedAt: number, cancellationReason?: string, cancellationReasonDetails?: string}) => void} [props.onRecordAnalytics] - Called when the user submits or cancels to capture analytics
 * @returns {import('react').ReactElement} The DescribeProblem component
 */
const DescribeProblem = ({
  conversationId,
  equipmentNumber,
  siteId,
  setSummaryText,
  onCloseSummaryGenerationErrorModal,
  onCloseRequestCompleted,
  followUpQuestions,
  setFollowUpQuestions,
  handleSubmitWorkOrder,
  downtimeStartTime,
  onRecordAnalytics,
}) => {
  const [isCancellationReasonDialogOpen, setIsCancellationReasonDialogOpen] = useState(false);

  const arrivedAtRef = useRef(0);
  const prevFollowUpQuestionsDataRef = useRef(undefined);

  const { t, i18n } = useTranslation();
  const userLanguage = /** @type {"en" | "es"} */ (i18n.language?.slice(0, 2));

  const {
    data: followUpQuestionsData,
    isLoading: loadingFollowUpQuestions,
    error: followUpQuestionsError,
    refetch: refetchFollowUpQuestions,
  } = useMaintenanceFollowUpQuestions({ conversationId, userLanguage });

  useEffect(() => {
    arrivedAtRef.current = getCurrentTimestamp();
  }, []);

  useEffect(() => {
    if (followUpQuestionsError) {
      toast.warning(t('followUpQuestionsErrorContent', { error: followUpQuestionsError?.message }));
    }
  }, [followUpQuestionsError, t]);

  const { mutate: fetchWorkOrderSummary, isPending: loadingWorkOrderSummary } = useWorkOrderSummary({
    onSuccess: (data) => {
      // Loading bar hidden by TanStack Query GlobalLoading
      setSummaryText(data?.summaryText);
    },
    onError: (err) => {
      // Loading bar hidden by TanStack Query GlobalLoading
      toast.error(t('aiSummaryErrorContent', { error: err?.message }));
      onCloseSummaryGenerationErrorModal();
    },
  });

  const { mutate: createTracking, isPending: loadingCreateTracking } = useCreateWorkOrderTracking({
    onSuccess: () => {
      // Loading bar hidden by TanStack Query GlobalLoading
      onCloseRequestCompleted();
    },
    onError: (err) => {
      // Loading bar hidden by TanStack Query GlobalLoading
      toast.error(t('closeRequestErrorContent', { error: err?.message }));
    },
  });

  const allQuestionsAnswered = followUpQuestions.every(
    (question) => question.selectedAnswer !== answerOptions.NONE_SELECTED,
  );

  const disabledCloseButton = loadingWorkOrderSummary || loadingCreateTracking || loadingFollowUpQuestions;

  const disabledSubmitButton = disabledCloseButton || !allQuestionsAnswered;

  useEffect(() => {
    const payload = /** @type {{questions?: Record<string, unknown>[]}} */ (followUpQuestionsData);
    const questions = Array.isArray(payload?.questions)
      ? /** @type {Record<string, unknown>[]} */ (payload.questions)
      : undefined;
    if (!questions || followUpQuestionsData === prevFollowUpQuestionsDataRef.current) return;

    prevFollowUpQuestionsDataRef.current = followUpQuestionsData;
    const setQuestions = /** @type {import('react').Dispatch<import('react').SetStateAction<any[]>>} */ (
      setFollowUpQuestions
    );
    setQuestions((prevFollowUpQuestions) =>
      questions.map((question) => ({
        ...question,
        selectedAnswer:
          prevFollowUpQuestions?.find((prevFollowUpQuestion) => prevFollowUpQuestion.id === question.id)
            ?.selectedAnswer ?? answerOptions.NONE_SELECTED,
        additionalDetails: '',
      })),
    );
  }, [followUpQuestionsData, setFollowUpQuestions]);

  const handleOnSelectAnswer = (selectedAnswer, questionId) => {
    const answeredAt = getCurrentTimestamp();
    setFollowUpQuestions(
      followUpQuestions.map((question) =>
        question.id === questionId
          ? {
              ...question,
              selectedAnswer: question.selectedAnswer === selectedAnswer ? answerOptions.NONE_SELECTED : selectedAnswer,
              additionalDetails: '',
              answeredAt: question.selectedAnswer === selectedAnswer ? null : answeredAt,
            }
          : question,
      ),
    );
  };

  const handleOnChangeAdditionalDetails = (event, questionId) => {
    setFollowUpQuestions(
      followUpQuestions.map((question) =>
        question.id === questionId
          ? {
              ...question,
              additionalDetails: event?.target?.value,
            }
          : question,
      ),
    );
  };

  const handleOnSubmitWorkOrder = () => {
    onRecordAnalytics?.({ outcome: 'submitted', followUpQuestions, arrivedAt: arrivedAtRef.current });

    const answeredQuestions = followUpQuestionsToQuestionAnswers(followUpQuestions);

    // Skip AI summary generation if no questions were answered
    if (!answeredQuestions.length) {
      // Call the parent component's handleSubmitWorkOrder function directly
      handleSubmitWorkOrder();
      return;
    }

    // Otherwise, proceed with AI summary generation as before
    // Loading bar shown by TanStack Query GlobalLoading
    fetchWorkOrderSummary({
      conversationId,
      questionAnswers: answeredQuestions,
      userLanguage,
    });
  };

  const handleOnCloseRequest = () => {
    setIsCancellationReasonDialogOpen(true);
  };

  const handleRefetchFollowUpQuestions = () => {
    refetchFollowUpQuestions();
  };

  const onCloseCancellationReasonDialog = () => {
    setIsCancellationReasonDialogOpen(false);
  };

  /**
   * Called when cancellation reason dialog is submitted
   * @param {string} selectedReason - The selected cancellation reason.
   * @param {string} additionalDetails - Additional details provided for the cancellation reason.
   */
  const onConfirmCancellationReasonDialog = (selectedReason, additionalDetails) => {
    if (!selectedReason) {
      return;
    }

    onRecordAnalytics?.({
      outcome: 'cancelled',
      followUpQuestions,
      arrivedAt: arrivedAtRef.current,
      cancellationReason: selectedReason,
      cancellationReasonDetails: additionalDetails,
    });

    // Loading bar shown by TanStack Query GlobalLoading
    createTracking({
      isRequestOpen: false,
      conversationId,
      questionAnswers: followUpQuestionsToQuestionAnswers(followUpQuestions),
      equipmentNumber,
      siteId,
      closureReason: selectedReason,
      closureReasonDetails: additionalDetails,
      downtimeStartTime,
      userLanguage,
    });
  };

  return (
    <>
      <div className={styles.column}>
        <div>
          <h6>{t('describeProblem')}</h6>
          <p>
            <Trans i18nKey='describeProblemInstructions' components={{ b: <b /> }} />
          </p>
          {followUpQuestionsError && !loadingFollowUpQuestions && (
            <div className={styles.marginY}>
              <Button
                data-testid='refetchFollowUpQuestionsButton'
                color='secondary'
                size='sm'
                onClick={handleRefetchFollowUpQuestions}
              >
                {t('refetchQuestions')}
              </Button>
            </div>
          )}
        </div>
        <div className={styles.questionsContainer}>
          {loadingFollowUpQuestions ? (
            <div className={styles.loadingBox}>{/* Loading state - implemented by TanStack Query GlobalLoading */}</div>
          ) : (
            followUpQuestions?.map(({ id, text, selectedAnswer, additionalDetails }) => (
              <AnyQuestionAnswers
                key={id}
                question={text}
                questionNumber={id}
                answerSelected={selectedAnswer}
                onSelectAnswer={(answer) => handleOnSelectAnswer(answer, id)}
                additionalDetails={additionalDetails}
                onChangeAdditionalDetails={(e) => handleOnChangeAdditionalDetails(e, id)}
              />
            ))
          )}
        </div>
        <div className={styles.stickyGrid}>
          <div>
            <Button color='secondary' size='sm' disabled={disabledSubmitButton} onClick={handleOnSubmitWorkOrder}>
              {t('submitWorkOrder')}
            </Button>
          </div>
          <div>
            <Button color='primary-alt' size='sm' disabled={disabledCloseButton} onClick={handleOnCloseRequest}>
              {loadingCreateTracking ? `${t('loading')}...` : t('closeRequest')}
            </Button>
          </div>
          {loadingCreateTracking && (
            <div className={styles.loadingBox}>{/* Loading state - implemented by TanStack Query GlobalLoading */}</div>
          )}
        </div>
      </div>
      <CancellationReasonDialog
        isOpen={isCancellationReasonDialogOpen}
        onClose={onCloseCancellationReasonDialog}
        onConfirm={onConfirmCancellationReasonDialog}
      />
    </>
  );
};
export default DescribeProblem;
